import { useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useCallback, useRef } from 'react';

import { Clips } from '@air/api';
import { ClipType } from '@air/api/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { defaultClipGetOptions, getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';
import { useUpdateBoardStats } from '~/utils/mutateUtils/BoardStats';
import { useAddAssetsToAllViews, useUpdateVersionByAssetId } from '~/utils/mutateUtils/GalleryAssets';
import { type SharedSyncTasksParams } from '~/utils/taskUtils/useSharedSyncTasks';

type OnCompleteParams = Parameters<
  NonNullable<SharedSyncTasksParams<'AssetTransformation', 'AssetTransformer'>['onComplete']>
>[0];

type AssetTransformationGalleryTask = OnCompleteParams['localTask'];

const getCompletedVersionId = (data: OnCompleteParams['task']['data']) => {
  if (data && 'versionId' in data && typeof data.versionId === 'string') {
    return data.versionId;
  }

  return undefined;
};

export const useAssetTransformationTaskGalleryUpdate = (isTask: (task: AssetTransformationGalleryTask) => boolean) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const { updateVersionByAssetId } = useUpdateVersionByAssetId();
  const { updateBoardStats } = useUpdateBoardStats();
  const completedLocalTaskIds = useRef(new Set<string>());

  const onTaskComplete = useCallback(
    async ({ task, localTask }: OnCompleteParams) => {
      if (!isTask(localTask)) {
        return;
      }

      const transformationTask = localTask;

      if (completedLocalTaskIds.current.has(transformationTask.localTaskId)) {
        return;
      }

      completedLocalTaskIds.current.add(transformationTask.localTaskId);

      const versionId = getCompletedVersionId(task.data);

      if (!versionId || !currentWorkspace?.id) {
        completedLocalTaskIds.current.delete(transformationTask.localTaskId);
        return;
      }

      const saveResultAs =
        'saveResultAs' in transformationTask.metadata ? transformationTask.metadata.saveResultAs : undefined;
      const boardId = saveResultAs?.options?.boardId;
      const clipGetOptions = { ...defaultClipGetOptions, boardId };

      try {
        const clip = await queryClient.ensureQueryData({
          queryKey: getClipWithoutWorkspaceIdKey({ clipId: versionId, options: clipGetOptions }),
          queryFn: () => Clips.getWithNoWorkspaceId({ id: versionId, options: clipGetOptions }),
        });

        if (saveResultAs?.as === 'assetVersion') {
          await updateVersionByAssetId(clip, boardId);
        } else if (saveResultAs?.as === 'asset') {
          await addAssetsToAllViews({
            parentBoardId: boardId,
            assets: [clip],
            type: getAssetGalleryItemType(clip),
          });

          if (boardId) {
            await updateBoardStats(boardId, (existingData) =>
              existingData
                ? produce(existingData, (draft) => {
                    draft.assetCount += clip.type === ClipType.nonMedia ? 0 : 1;
                    draft.fileCount += clip.type === ClipType.nonMedia ? 1 : 0;
                    draft.sizeBytes += clip.size;
                  })
                : {
                    assetCount: clip.type !== ClipType.nonMedia ? 1 : 0,
                    fileCount: clip.type === ClipType.nonMedia ? 1 : 0,
                    sizeBytes: clip.size,
                  },
            );
          }
        }

        return clip;
      } catch (error) {
        completedLocalTaskIds.current.delete(transformationTask.localTaskId);
        throw error;
      }
    },
    [addAssetsToAllViews, currentWorkspace?.id, isTask, queryClient, updateBoardStats, updateVersionByAssetId],
  );

  return { onTaskComplete };
};
